import { APICfg } from 'services/api/api'
import { convertEmptyStringToUndefined } from 'services/utils'
import { getMonthAndYearFromDate } from 'services/utils/date'
import { ProductServiceName } from 'types/common'
import {
  ProfessionalServiceToAPI,
  ServiceToAPI,
  TransactionToAPI,
} from './types/api/request-models'
import {
  APITransaction,
  APITransactionProfessionalService,
  APITransactionServices,
} from './types/api/response-models'
import {
  Transaction,
  TransactionService,
} from './types/internal/transaction-models'

export const NAMED_SERVICES: ProductServiceName[] = [
  'drinkService',
  'foodService',
  'locationService',
  'musicService',
  'rentService',
  'virtualService',
  'physicalService',
]

// ============
// PARSE
// ============

const transactionParse = ({
  discountPerc,
  eventDate,
  professionalServices,
  ...rest
}: APITransaction): Transaction => {
  const parsedNamedServices = NAMED_SERVICES.map(namedServiceParse(rest))

  const parsedProfessionalService =
    professionalServices?.map(professionalServiceParse) ?? []

  return {
    ...rest,
    eventDate,
    discountPerc: discountPerc ?? 0,
    services: [...parsedNamedServices, ...parsedProfessionalService],
    monthYearDate: getMonthAndYearFromDate(eventDate),
  }
}

const namedServiceParse =
  (
    transactionServicesMap: Omit<APITransactionServices, 'professionalServices'>
  ) =>
  (service: ProductServiceName): TransactionService => ({
    ...transactionServicesMap[service],
    supplier: transactionServicesMap[service].supplier?.name,
    serviceName: service,
  })

const professionalServiceParse = ({
  sectionId,
  sectionName,
  specialType,
  supplier,
  ...rest
}: APITransactionProfessionalService): TransactionService => ({
  ...rest,
  serviceName: sectionName,
  section: sectionId,
  supplier: supplier?.name,
})

// ============
// WRITE
// ============

const transactionWrite = async ({
  services,
  couponCode,
  invoiceStatus,
  poNumber,
  customizations,
  paymentStatus,
  accountingStatus,
  invoiceNumber,
  invoiceExpiryDate,
}: Transaction): Promise<TransactionToAPI> => ({
  paymentStatus,
  invoiceStatus,
  customizations,
  accountingStatus,
  invoiceExpiryDate,
  ...namedServicesWrite(services),
  poNumber: convertEmptyStringToUndefined(poNumber),
  couponCode: convertEmptyStringToUndefined(couponCode),
  professionalServices: professionalServiceWrite(services),
  invoiceNumber: convertEmptyStringToUndefined(invoiceNumber),
})

const namedServicesWrite = (
  services: TransactionService[]
): Record<ProductServiceName, ServiceToAPI> =>
  NAMED_SERVICES.reduce(
    (acc, service) => ({
      ...acc,
      [service]: services.find((s) => s.serviceName === service),
    }),
    Object.create(null)
  )

const professionalServiceWrite = (
  services: TransactionService[]
): ProfessionalServiceToAPI[] =>
  services
    .filter(
      (service) =>
        !NAMED_SERVICES.includes(service.serviceName as ProductServiceName)
    )
    .map(({ costNoVat, costVatPerc, customization, section }) => ({
      costNoVat,
      costVatPerc,
      customization,
      section: section!,
    }))

// ============
// CFG
// ============

const apiCfg: APICfg = {
  endpoint: 'event-account',
  UPDATE_METHOD: 'PATCH' as const,
  parse: transactionParse,
  write: transactionWrite,
}

export default apiCfg
